import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getMessagingHub } from '@/lib/messaging-hub';

// Owns the employer's unread-conversation badge. EmployerSubnav (which renders
// the tab) and the /employer/messages page are siblings with no prop path, so
// the count lives here — same reasoning as employerSession.
//
// Freshness: SignalR 'unreadChanged' pushes + a slow jittered poll as the
// fallback when the socket can't connect. start() is idempotent.
const POLL_BASE_MS = 60_000;
const POLL_JITTER_MS = 30_000;

export const useEmployerMessagesStore = defineStore('employerMessages', () => {
    const unreadCount = ref(0);
    let started = false;
    let pollTimer = 0;
    let offUnread: (() => void) | null = null;
    let offReconnected: (() => void) | null = null;

    async function fetchUnread(basePath = '') {
        try {
            const res = await fetch(`${basePath}/api/employer/messages/unread-count`, { credentials: 'include' });
            if (!res.ok) return;
            const data = await res.json();
            if (data.success) unreadCount.value = data.unread || 0;
        } catch { /* network blip — next poll/push corrects it */ }
    }

    function schedulePoll(basePath: string) {
        const delay = POLL_BASE_MS + (Math.random() - 0.5) * POLL_JITTER_MS;
        pollTimer = window.setTimeout(() => {
            void fetchUnread(basePath);
            schedulePoll(basePath);
        }, delay);
    }

    function start(basePath = '') {
        if (started) return;
        started = true;
        void fetchUnread(basePath);
        schedulePoll(basePath);
        const hub = getMessagingHub();
        offUnread = hub.on('unreadChanged', () => void fetchUnread(basePath));
        offReconnected = hub.on('reconnected', () => void fetchUnread(basePath));
    }

    function stop() {
        if (!started) return;
        started = false;
        window.clearTimeout(pollTimer);
        offUnread?.();
        offReconnected?.();
        offUnread = null;
        offReconnected = null;
    }

    return { unreadCount, fetchUnread, start, stop };
});
